/**
 * useViewJobs — My Jobs tabs (Applied, Invited, Recommended, Saved).
 * Mirrors Flutter WebViewJobsBloc (lib/application/web_profile/web_view_jobs/web_view_jobs_bloc.dart):
 *   - every tab change reloads that tab (page size 6), logs "MyJobs" click_*_jobs and pushes the
 *     tab's URL (history.pushState — no screen_view)
 *   - after each load the applied-saved API marks the saved jobs (_GetSavedAppliedJobs)
 *   - save / unsave with the candidate payload, per-job loading, saved-count refresh
 *   - Recommended: filters + sort (relevance by default), aggregations, and a 404 means
 *     "upload a resume first" (showUploadResume) → upload → reload the jobs (onRecommendedJobs)
 *   - opening a job / application logs screen_view with the tab's analyticsClass
 *     (NavigationService.navigateTo) before opening it in a new tab
 *   - Applied filter (All / Pending / Applied) is applied client-side by the page
 *   - language change reloads the current tab
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { auth } from '@/config/firebase';
import { ROUTES } from '@/app/router/routes';
import {
  getAppliedJobs,
  getInvitedJobs,
  getRecommendedJobs,
  getRecommendedJobsAggregation,
  getSavedJobs,
} from '@/services/api/profileApi';
import { EMPTY_FILE_ERROR, uploadResumeStandalone } from '@/services/api/applicationApi';
import { getUserDetails } from '@/services/api/authApi';
import {
  getAppliedSavedJobs,
  getSavedJobsCount,
  saveJob,
  unsaveJob,
} from '@/services/api/homeApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import { useAppStateStore } from '@/store/appStateStore';
import { useLocalizationStore } from '@/store/localizationStore';
import {
  APPLICATION_PAGE,
  JOB_SHARE_REF,
  resetApplicationPageAndType,
  setApplicationPageAndType,
} from '@/features/jobs/utils/jobOrigin';
import {
  applyFilterSelection,
  defaultSortFilters,
  sortSelectedOnTop,
  toFilterParams,
  type SelectedFilters,
} from '@/features/jobs/utils/filterState';
import { validateResumeFile } from '@/features/jobs/utils/resumeFile';
import type { Aggregation, AggregationValue } from '@/types/job.types';
import type { ViewJobsData } from '@/types/profile.types';
import { ViewJobsTab } from '@/types/profile.types';
import { setRelevanceData } from '@/features/jobs/utils/relevanceData';

const PAGE_SIZE = 6;

// Flutter ErrorConstants texts
const SAVE_JOB_SUCCESS = 'Saved the job successfully';
const SAVE_JOB_FAILED = 'Failed to save the job!';
const UNSAVE_JOB_SUCCESS = 'Unsaved the job successfully';
const UNSAVE_JOB_FAILED = 'Failed to unsave the job!';
const GENERIC_ERROR = 'Something went wrong, Please try again!';

const TAB_ROUTES: Record<ViewJobsTab, string> = {
  [ViewJobsTab.APPLIED]: ROUTES.VIEW_JOBS_APPLIED,
  [ViewJobsTab.INVITED]: ROUTES.VIEW_JOBS_INVITED,
  [ViewJobsTab.RECOMMENDED]: ROUTES.VIEW_JOBS_RECOMMENDED,
  [ViewJobsTab.SAVED]: ROUTES.VIEW_JOBS_SAVED,
};

/**
 * analyticsClass passed to navigateTo by each tab widget (web_applied_jobs.dart, …). The
 * Recommended tab passes none when it opens a job, only when it opens an application.
 */
const JOB_SCREEN_CLASS: Record<ViewJobsTab, string | undefined> = {
  [ViewJobsTab.APPLIED]: 'applied_jobs_page',
  [ViewJobsTab.INVITED]: 'invited_jobs_page',
  [ViewJobsTab.RECOMMENDED]: undefined,
  [ViewJobsTab.SAVED]: 'saved_jobs_page',
};

const APPLICATION_SCREEN_CLASS: Record<ViewJobsTab, string> = {
  [ViewJobsTab.APPLIED]: 'applied_jobs_page',
  [ViewJobsTab.INVITED]: 'invited_jobs_page',
  [ViewJobsTab.RECOMMENDED]: 'recommended_jobs_page',
  [ViewJobsTab.SAVED]: 'saved_jobs_page',
};

const TAB_EVENTS: Record<ViewJobsTab, string> = {
  [ViewJobsTab.APPLIED]: 'click_applied_jobs',
  [ViewJobsTab.INVITED]: 'click_invited_jobs',
  [ViewJobsTab.RECOMMENDED]: 'click_recommended_jobs',
  [ViewJobsTab.SAVED]: 'click_saved_jobs',
};

export const ALL_TABS = [
  ViewJobsTab.APPLIED,
  ViewJobsTab.INVITED,
  ViewJobsTab.RECOMMENDED,
  ViewJobsTab.SAVED,
] as const;

export interface TabList {
  jobs: ViewJobsData[];
  /** Next offset (Applied / Invited / Saved) or page (Recommended) */
  next: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
}

const firstPage = (tab: ViewJobsTab) => (tab === ViewJobsTab.RECOMMENDED ? 1 : 0);

const emptyList = (tab: ViewJobsTab): TabList => ({
  jobs: [],
  next: firstPage(tab),
  hasMore: false,
  isLoading: true,
  isLoadingMore: false,
});

interface Toast {
  /** Flutter's English text — Toast translates it */
  message: string;
  type: 'success' | 'error';
}

/** Application origin per tab — Applied resets it, Invited / Recommended / Saved set their page. */
function setApplicationOrigin(tab: ViewJobsTab): void {
  if (tab === ViewJobsTab.APPLIED) resetApplicationPageAndType();
  else if (tab === ViewJobsTab.INVITED) setApplicationPageAndType(APPLICATION_PAGE.CAREER_RECOMMENDATION_MAIL, false);
  else if (tab === ViewJobsTab.RECOMMENDED) setApplicationPageAndType(APPLICATION_PAGE.CAREER_RECOMMENDATION, true);
  else setApplicationPageAndType(APPLICATION_PAGE.CAREER_SEARCH, false);
}

/** One page of a tab. */
async function fetchTabPage(
  tab: ViewJobsTab,
  next: number,
  filters: SelectedFilters,
): Promise<{ jobs: ViewJobsData[]; next: number; hasMore: boolean }> {
  if (tab === ViewJobsTab.RECOMMENDED) {
    // Flutter's load-more drops the filters and sort on page 2+; React keeps them (D2b)
    const res = await getRecommendedJobs(next, PAGE_SIZE, toFilterParams(filters));
    const jobs = res?.entities ?? [];
    return { jobs, next: next + 1, hasMore: (res?.total ?? 0) > next * PAGE_SIZE };
  }
  const load =
    tab === ViewJobsTab.APPLIED ? getAppliedJobs : tab === ViewJobsTab.INVITED ? getInvitedJobs : getSavedJobs;
  const jobs = await load(next, PAGE_SIZE);
  return { jobs, next: next + PAGE_SIZE, hasMore: jobs.length >= PAGE_SIZE };
}

/** Flutter _GetSavedAppliedJobs — the displayIds the candidate has saved (null if unavailable). */
async function fetchSavedIds(): Promise<Set<string> | null> {
  try {
    const res = await getAppliedSavedJobs();
    return new Set(res.appliedSavedJobs?.Saved_Jobs ?? []);
  } catch {
    return null;
  }
}

export function useViewJobs(initialTab: ViewJobsTab) {
  const navigate = useNavigate();
  const currentLanguage = useLocalizationStore((s) => s.currentLanguage);

  const [activeTab, setActiveTabState] = useState<ViewJobsTab>(initialTab);
  /** Set with the state, so a URL change rendered before the state doesn't look like back / forward */
  const activeTabRef = useRef(initialTab);
  const [lists, setLists] = useState<Record<ViewJobsTab, TabList>>(() => ({
    [ViewJobsTab.APPLIED]: emptyList(ViewJobsTab.APPLIED),
    [ViewJobsTab.INVITED]: emptyList(ViewJobsTab.INVITED),
    [ViewJobsTab.RECOMMENDED]: emptyList(ViewJobsTab.RECOMMENDED),
    [ViewJobsTab.SAVED]: emptyList(ViewJobsTab.SAVED),
  }));

  // Recommended tab
  const [showUploadResume, setShowUploadResume] = useState(false);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [recommendedAggregations, setRecommendedAggregations] = useState<Aggregation[]>([]);
  const [isLoadingAggregations, setIsLoadingAggregations] = useState(false);
  const [recommendedFilters, setRecommendedFilters] = useState<SelectedFilters>(() =>
    defaultSortFilters(true),
  );

  const [toast, setToast] = useState<Toast | null>(null);

  const listsRef = useRef(lists);
  listsRef.current = lists;
  const filtersRef = useRef(recommendedFilters);
  filtersRef.current = recommendedFilters;
  /** Latest request per tab — older responses (e.g. before a filter change) are dropped */
  const requestIds = useRef<Record<ViewJobsTab, number>>({ 0: 0, 1: 0, 2: 0, 3: 0 });

  const patchList = useCallback((tab: ViewJobsTab, patch: Partial<TabList> | ((l: TabList) => Partial<TabList>)) => {
    setLists((prev) => ({
      ...prev,
      [tab]: { ...prev[tab], ...(typeof patch === 'function' ? patch(prev[tab]) : patch) },
    }));
  }, []);

  const patchJob = useCallback(
    (tab: ViewJobsTab, displayId: string | undefined, patch: Partial<ViewJobsData>) => {
      patchList(tab, (l) => ({
        jobs: l.jobs.map((j) => (j.displayId === displayId ? { ...j, ...patch } : j)),
      }));
    },
    [patchList],
  );

  // ── Loading ──────────────────────────────────────────────────

  const loadTab = useCallback(
    async (tab: ViewJobsTab, append = false, filters: SelectedFilters = filtersRef.current) => {
      if (!auth.currentUser) return;
      const requestId = ++requestIds.current[tab];
      const start = append ? listsRef.current[tab].next : firstPage(tab);
      patchList(tab, append ? { isLoadingMore: true } : { isLoading: true });

      try {
        const [page, savedIds] = await Promise.all([fetchTabPage(tab, start, filters), fetchSavedIds()]);
        if (requestId !== requestIds.current[tab]) return;

        const jobs = page.jobs.map((job) => ({
          ...job,
          isSavedJob:
            tab === ViewJobsTab.SAVED || !!job.isSavedJob || !!(job.displayId && savedIds?.has(job.displayId)),
        }));
        patchList(tab, (l) => ({
          jobs: append ? [...l.jobs, ...jobs] : jobs,
          next: page.next,
          hasMore: page.hasMore,
          isLoading: false,
          isLoadingMore: false,
        }));
        if (tab === ViewJobsTab.RECOMMENDED) setShowUploadResume(false);
      } catch (error) {
        if (requestId !== requestIds.current[tab]) return;
        // Flutter IApplicationRepository.getRecommendedJobs: 404 → 'UploadResume'
        if (tab === ViewJobsTab.RECOMMENDED && !append && isAxiosError(error) && error.response?.status === 404) {
          setShowUploadResume(true);
          patchList(tab, { jobs: [], hasMore: false, isLoading: false, isLoadingMore: false });
          return;
        }
        patchList(tab, { isLoading: false, isLoadingMore: false, ...(append ? { hasMore: false } : {}) });
        setToast({ message: GENERIC_ERROR, type: 'error' });
      }
    },
    [patchList],
  );

  /** Flutter _GetRecommendedJobsAggregation (failures leave the filters out). */
  const loadRecommendedAggregations = useCallback(async (filters: SelectedFilters) => {
    setIsLoadingAggregations(true);
    try {
      const res = await getRecommendedJobsAggregation(toFilterParams(filters));
      setRecommendedAggregations(sortSelectedOnTop(res?.aggregations ?? [], filters));
    } catch {
      setRecommendedAggregations([]);
    } finally {
      setIsLoadingAggregations(false);
    }
  }, []);

  /**
   * Flutter _OnTabChange. `history`: 'push' for a tab click (Flutter history.pushState, which
   * logs no screen_view), 'replace' on first load (so /viewJobs shows its tab's URL without
   * adding an entry), 'none' when the URL already changed (browser back / forward).
   */
  const openTab = useCallback(
    (tab: ViewJobsTab, history: 'push' | 'replace' | 'none' = 'push') => {
      activeTabRef.current = tab;
      setActiveTabState(tab);
      if (history !== 'none' && window.location.pathname !== TAB_ROUTES[tab]) {
        navigate(TAB_ROUTES[tab], { replace: history === 'replace' });
      }
      void analyticsService.logEvent('MyJobs', TAB_EVENTS[tab]);
      void loadTab(tab);
      if (tab === ViewJobsTab.RECOMMENDED) void loadRecommendedAggregations(filtersRef.current);
    },
    [navigate, loadTab, loadRecommendedAggregations],
  );

  /** A tab click */
  const selectTab = useCallback((tab: ViewJobsTab) => openTab(tab, 'push'), [openTab]);

  /** The URL moved to another tab (browser back / forward) — show it without touching history. */
  const syncTabFromUrl = useCallback(
    (tab: ViewJobsTab) => {
      if (tab !== activeTabRef.current) openTab(tab, 'none');
    },
    [openTab],
  );

  const loadMore = useCallback(() => {
    const list = listsRef.current[activeTab];
    if (!list.hasMore || list.isLoading || list.isLoadingMore) return;
    void loadTab(activeTab, true);
  }, [activeTab, loadTab]);

  // ── Init (Flutter _Init) + language change (languageStream → init) ──
  const languageRef = useRef(currentLanguage);
  useEffect(() => {
    if (!auth.currentUser) return;
    openTab(activeTab, 'replace');
    void getSavedJobsCount().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (languageRef.current === currentLanguage) return;
    languageRef.current = currentLanguage;
    openTab(activeTab, 'none');
  }, [currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save / unsave (Flutter _OnClickSaveJob / _OnClickUnsaveJob and the *OnRecommended variants) ──

  const saveJobOnTab = useCallback(
    async (tab: ViewJobsTab, job: ViewJobsData) => {
      patchJob(tab, job.displayId, { isLoadingForSaveJobButton: true });
      try {
        const candidate = useAppStateStore.getState().candidateDetails ?? (await getUserDetails());
        const c = candidate as Record<string, unknown>;
        await saveJob({
          candidateId: c.id,
          requisitionId: job.id,
          candidateDisplayId: c.displayId,
          requisitionDisplayId: job.displayId,
          primaryEmailId: candidate.primaryEmailId,
          exprncFromMnthInYears: job.requiredExperienceInMonths?.from,
          exprncToMnthInYears: job.requiredExperienceInMonths?.to,
        });
        patchJob(tab, job.displayId, { isLoadingForSaveJobButton: false, isSavedJob: true });
        setToast({ message: SAVE_JOB_SUCCESS, type: 'success' });
      } catch {
        patchJob(tab, job.displayId, { isLoadingForSaveJobButton: false });
        setToast({ message: SAVE_JOB_FAILED, type: 'error' });
      }
      void analyticsService.logJobSave({
        location: tab === ViewJobsTab.RECOMMENDED ? 'Recommended Jobs' : 'Web View Jobs',
        jobId: job.id ?? '',
        jobTitle: job.jobTitle ?? '',
      });
      void getSavedJobsCount().catch(() => {});
    },
    [patchJob],
  );

  const unsaveJobOnTab = useCallback(
    async (tab: ViewJobsTab, job: ViewJobsData) => {
      patchJob(tab, job.displayId, { isLoadingForSaveJobButton: true });
      try {
        const ok = await unsaveJob(job.displayId ?? '');
        if (!ok) throw new Error('unsave failed');
        if (tab === ViewJobsTab.SAVED) {
          patchList(tab, (l) => ({ jobs: l.jobs.filter((j) => j.displayId !== job.displayId) }));
        } else {
          patchJob(tab, job.displayId, { isLoadingForSaveJobButton: false, isSavedJob: false });
        }
        setToast({ message: UNSAVE_JOB_SUCCESS, type: 'success' });
      } catch {
        patchJob(tab, job.displayId, { isLoadingForSaveJobButton: false });
        setToast({ message: UNSAVE_JOB_FAILED, type: 'error' });
      }
      void analyticsService.logJobUnsave({
        location: tab === ViewJobsTab.RECOMMENDED ? 'Recommended Jobs' : 'Web View Jobs',
        jobId: job.id ?? '',
        jobTitle: job.jobTitle ?? '',
      });
      void getSavedJobsCount().catch(() => {});
    },
    [patchJob, patchList],
  );

  // ── Navigation (card tap → job page, apply → application form), per Flutter tab widgets ──

  const openJob = useCallback((tab: ViewJobsTab, job: ViewJobsData) => {
    if (!job.displayId) return;
    if (tab === ViewJobsTab.RECOMMENDED) {
      // Flutter _OnClickSetRelevanceData — read back by the job page
      setRelevanceData(job);
    }
    setApplicationOrigin(tab);
    void analyticsService.logScreenView({ screenName: ROUTES.JOBS, screenClass: JOB_SCREEN_CLASS[tab] });
    window.open(`${ROUTES.JOBS}/${job.displayId}?ref=${JOB_SHARE_REF.INTERNAL}`, '_blank');
  }, []);

  /** Flutter onEditApplication(isEasyApply) — also resumes drafts / edits submitted applications. */
  const openApplication = useCallback((tab: ViewJobsTab, job: ViewJobsData, isEasyApply: boolean) => {
    if (!job.displayId) return;
    setApplicationOrigin(tab);
    const route = isEasyApply ? ROUTES.EASY_APPLY : ROUTES.APPLICATION;
    void analyticsService.logScreenView({ screenName: route, screenClass: APPLICATION_SCREEN_CLASS[tab] });
    window.open(`${route}/${job.displayId}`, '_blank');
  }, []);

  // ── Recommended filters (Flutter _OnFilterSelection / _OnClearFilter / _OnApplyFilters / _ClearFilters) ──

  const applyRecommendedFilters = useCallback(
    (filters: SelectedFilters) => {
      filtersRef.current = filters;
      setRecommendedFilters(filters);
      void loadTab(ViewJobsTab.RECOMMENDED, false, filters);
      void loadRecommendedAggregations(filters);
      void analyticsService.logJobSearch({ searchTerm: 'Recommended Jobs Filters', filters });
    },
    [loadTab, loadRecommendedAggregations],
  );

  const onRecommendedFilterSelection = useCallback((values: AggregationValue[]) => {
    setRecommendedFilters((prev) => applyFilterSelection(prev, values));
  }, []);

  const onClearRecommendedFilter = useCallback((key: string) => {
    setRecommendedFilters((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const onApplyRecommendedFilters = useCallback(() => {
    applyRecommendedFilters(filtersRef.current);
  }, [applyRecommendedFilters]);

  const onSelectRecommendedSort = useCallback(
    (value: AggregationValue) => {
      applyRecommendedFilters(applyFilterSelection(filtersRef.current, [value]));
    },
    [applyRecommendedFilters],
  );

  const onClearRecommendedFilters = useCallback(() => {
    applyRecommendedFilters(defaultSortFilters(true));
    void analyticsService.logEvent('Recommended Jobs', 'clear_filters');
  }, [applyRecommendedFilters]);

  // ── Resume upload (Flutter _SelectFilesFromSystem → _UploadResume → onRecommendedJobs) ──

  const uploadResume = useCallback(
    async (file: File) => {
      const fileError = validateResumeFile(file);
      if (fileError) {
        setToast({ message: fileError, type: 'error' });
        return;
      }
      setIsUploadingResume(true);
      try {
        await uploadResumeStandalone(file);
      } catch (error) {
        // Flutter shows "Failed to upload a file!" and then showMessage on top of it:
        // ErrorConstants.emptyFile (204) or genericError — the visible one is the latter
        const isEmptyFile = error instanceof Error && error.message === EMPTY_FILE_ERROR;
        setToast({ message: isEmptyFile ? EMPTY_FILE_ERROR : GENERIC_ERROR, type: 'error' });
        setIsUploadingResume(false);
        return;
      }
      setIsUploadingResume(false);
      void analyticsService.logEvent('Profile', 'uploaded_resume');
      // resumeUploadStatus listener → onRecommendedJobs only (the aggregations are not reloaded)
      void loadTab(ViewJobsTab.RECOMMENDED);
    },
    [loadTab],
  );

  const clearToast = useCallback(() => setToast(null), []);

  return {
    state: {
      activeTab,
      lists,
      showUploadResume,
      isUploadingResume,
      recommendedAggregations,
      recommendedFilters,
      isLoadingAggregations,
      toast,
    },
    actions: {
      setActiveTab: selectTab,
      syncTabFromUrl,
      loadMore,
      saveJob: saveJobOnTab,
      unsaveJob: unsaveJobOnTab,
      openJob,
      openApplication,
      onRecommendedFilterSelection,
      onClearRecommendedFilter,
      onApplyRecommendedFilters,
      onSelectRecommendedSort,
      onClearRecommendedFilters,
      uploadResume,
      clearToast,
    },
  };
}

